import { useMemo } from 'react'
import { useQuery, useQueries } from '@tanstack/react-query'
import { useStore } from '@nanostores/react'
import { AccountModule } from '@core/modules/account/account.module'
import { SalesforceMdrGatewayService } from '@core/services/api-gateway/salesforce-mdr-gateway-service/salesforce-mdr-gateway.service'
import { mdrQueryKeys } from '@core/modules/mdr/mdr.query-keys'
import type { TCase, TGetCasesPageResult } from '@core/modules/mdr/mdr'

export function useMdrCasesQuery() {
    const { isMdrActive, currentAccount } = useStore(AccountModule.store)
    const accountId = currentAccount?.id ?? ''
    const isMock = !isMdrActive

    const firstPageQuery = useQuery<TGetCasesPageResult>({
        queryKey: mdrQueryKeys.cases(accountId, isMock),
        queryFn: async () => {
            const result = await SalesforceMdrGatewayService.getAccountCases(
                isMock,
                1
            )
            if (!result) throw new Error('Failed to load MDR cases')
            return result
        },
        enabled: !!accountId,
    })

    const totalPages = firstPageQuery.data?.pagination?.total_pages ?? 1

    const remainingPageNumbers =
        totalPages > 1
            ? Array.from({ length: totalPages - 1 }, (_, i) => i + 2)
            : []

    const { isLoadingMore, pagesData } = useQueries({
        queries: remainingPageNumbers.map((page) => ({
            queryKey: mdrQueryKeys.casesPage(accountId, isMock, page),
            queryFn: () =>
                SalesforceMdrGatewayService.getAccountCases(isMock, page),
            enabled: !!accountId,
        })),
        combine: (results) => {
            if (results.length === 0)
                return { isLoadingMore: false, pagesData: null }

            const isLoadingMore = results.some((q) => q.isPending)
            const allSettled = results.every((q) => q.isSuccess || q.isError)

            return {
                isLoadingMore,
                pagesData: allSettled
                    ? results.reduce<TCase>(
                          (acc, q) => {
                              if (!q.data) return acc
                              return {
                                  alerts: [
                                      ...acc.alerts,
                                      ...q.data.data.alerts,
                                  ],
                                  incidents: [
                                      ...acc.incidents,
                                      ...q.data.data.incidents,
                                  ],
                                  serviceRequests: [
                                      ...acc.serviceRequests,
                                      ...q.data.data.serviceRequests,
                                  ],
                              }
                          },
                          { alerts: [], incidents: [], serviceRequests: [] }
                      )
                    : null,
            }
        },
    })

    const data = useMemo<TCase | undefined>(() => {
        if (!firstPageQuery.data) return undefined
        if (!pagesData) return firstPageQuery.data.data
        return {
            alerts: [...firstPageQuery.data.data.alerts, ...pagesData.alerts],
            incidents: [
                ...firstPageQuery.data.data.incidents,
                ...pagesData.incidents,
            ],
            serviceRequests: [
                ...firstPageQuery.data.data.serviceRequests,
                ...pagesData.serviceRequests,
            ],
        }
    }, [firstPageQuery.data, pagesData])

    return {
        data,
        isLoading: firstPageQuery.isLoading,
        isLoadingMore,
        isError: firstPageQuery.isError,
        isSuccess: firstPageQuery.isSuccess,
        fetchStatus: firstPageQuery.fetchStatus,
        error: firstPageQuery.error,
    }
}
